var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useEffect, useMemo, useReducer, useState } from 'react';
import produce from 'immer';
import FilterTypes from '@constants/FilterTypes';
import { translate } from '@utils/i18n';
import { actionTypes as dateRangeActionTypes, reducerFunction as dateRangeReducerFunction } from '@components/Kendo/DateRangePickerReducer';
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import { loadCacheFromLocalStorage, saveCacheToLocalStorage } from '../../../helpers/localStorage';
import { INITIAL_FILTER_STATE } from '../types';
import { NAMESPACE } from '../constants';
const BASE_CACHE_KEY = 'kendo/sprinkler_projection/index';
const DATE_TYPE_OPTIONS = [
    { value: 'due', label: translate('filters.date_type.due', { namespace: NAMESPACE }) },
    { value: 'scheduled', label: translate('filters.date_type.scheduled', { namespace: NAMESPACE }) }
];
const INITIAL_SORT = { sortBy: null, sortDirection: 'asc' };
function loadInitialHiddenColumns(cacheKey) {
    var _a;
    const cache = loadCacheFromLocalStorage(cacheKey);
    return (_a = cache === null || cache === void 0 ? void 0 : cache.hiddenColumns) !== null && _a !== void 0 ? _a : [];
}
function loadInitialState(cacheKey) {
    const cache = loadCacheFromLocalStorage(cacheKey);
    if (!cache)
        return INITIAL_FILTER_STATE;
    return Object.assign(Object.assign(Object.assign({}, INITIAL_FILTER_STATE), cache), { search: '' });
}
function loadInitialSort(cacheKey) {
    var _a;
    const cache = loadCacheFromLocalStorage(cacheKey);
    if (!(cache === null || cache === void 0 ? void 0 : cache.sortBy))
        return INITIAL_SORT;
    return { sortBy: cache.sortBy, sortDirection: (_a = cache.sortDirection) !== null && _a !== void 0 ? _a : 'asc' };
}
const initialActive = {
    accountIds: true,
    buildingIds: true,
    territoryIds: true,
    additionalTerritoryIds: true,
    systemIds: true,
    dateType: true,
    dateRange: true
};
function filtersReducer(state, action) {
    if (action.type === 'RESET')
        return INITIAL_FILTER_STATE;
    if (action.type === dateRangeActionTypes.DATE_RANGE_FILTER_CHANGED) {
        return produce({ filters: state }, (draft) => {
            dateRangeReducerFunction(draft, action);
        }).filters;
    }
    return Object.assign(Object.assign({}, state), { [action.field]: action.value });
}
function activeReducer(state, action) {
    if (action.type === 'RESET')
        return initialActive;
    return Object.assign(Object.assign({}, state), { [action.field]: !state[action.field] });
}
export function useFilters(buildingId) {
    var _a;
    const cacheKey = useMemo(() => (buildingId ? `${BASE_CACHE_KEY}/building-${buildingId}` : BASE_CACHE_KEY), [buildingId]);
    const [filters, dispatch] = useReducer(filtersReducer, undefined, () => loadInitialState(cacheKey));
    const [active, dispatchActive] = useReducer(activeReducer, initialActive);
    const [sort, setSort] = useState(() => loadInitialSort(cacheKey));
    const [hiddenColumns, setHiddenColumns] = useState(() => loadInitialHiddenColumns(cacheKey));
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { search: _search } = filters, toCache = __rest(filters, ["search"]);
        saveCacheToLocalStorage(cacheKey, Object.assign(Object.assign(Object.assign({}, toCache), sort), { hiddenColumns }));
    }, [filters, sort, hiddenColumns, cacheKey]);
    const setFilter = (field, value) => dispatch({ type: 'SET_FILTER', field, value });
    const setSearch = (value) => setFilter('search', value);
    const setSortBy = (by, direction = 'asc') => setSort({ sortBy: by, sortDirection: direction });
    const resetFilters = () => {
        dispatch({ type: 'RESET' });
        dispatchActive({ type: 'RESET' });
        setSort(INITIAL_SORT);
        setHiddenColumns([]);
    };
    const onResetCache = () => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(translate('are_you_sure', { namespace: 'generic' })))
            return;
        localStorage.removeItem(cacheKey);
        window.location.reload();
    };
    const toggle = (field) => dispatchActive({ type: 'TOGGLE', field });
    const filtersList = [
        {
            field: 'accountIds',
            locale: 'account',
            type: buildingId ? FilterTypes.Hidden : FilterTypes.GenericMultiSelect,
            active: active.accountIds,
            value: filters.accountIds,
            onChange: (v) => setFilter('accountIds', v),
            onToggle: () => toggle('accountIds')
        },
        {
            field: 'buildingIds',
            locale: 'building',
            type: buildingId ? FilterTypes.Hidden : FilterTypes.GenericMultiSelect,
            active: active.buildingIds,
            value: filters.buildingIds,
            onChange: (v) => setFilter('buildingIds', v),
            onToggle: () => toggle('buildingIds')
        }
    ];
    if (!hasGlobalTerritoriesApplied()) {
        filtersList.push({
            field: 'territory',
            locale: 'territory',
            type: buildingId ? FilterTypes.Hidden : FilterTypes.GenericMultiSelect,
            active: active.territoryIds,
            value: filters.territoryIds,
            onChange: (v) => setFilter('territoryIds', v),
            onToggle: () => toggle('territoryIds')
        });
    }
    filtersList.push({
        field: 'additionalTerritory',
        locale: 'additional_territory',
        backendField: 'additional_territory',
        type: buildingId ? FilterTypes.Hidden : FilterTypes.GenericMultiSelect,
        active: active.additionalTerritoryIds,
        value: filters.additionalTerritoryIds,
        onChange: (v) => setFilter('additionalTerritoryIds', v),
        onToggle: () => toggle('additionalTerritoryIds')
    }, {
        field: 'system',
        locale: 'system',
        backendField: 'sprinkler_system',
        buildingId,
        type: FilterTypes.GenericMultiSelect,
        active: active.systemIds,
        value: filters.systemIds,
        onChange: (v) => setFilter('systemIds', v),
        onToggle: () => toggle('systemIds')
    }, {
        field: 'dateType',
        locale: 'date_type',
        type: 'commonSelect',
        active: active.dateType,
        value: (_a = filters.dateType) !== null && _a !== void 0 ? _a : '',
        optionsForSelect: DATE_TYPE_OPTIONS,
        onChange: (v) => setFilter('dateType', (v || null)),
        onToggle: () => toggle('dateType')
    }, {
        field: 'dateRange',
        locale: 'date_range',
        type: 'dateRangePicker',
        active: active.dateRange,
        values: filters.dateRange,
        onChange: (dateField, value) => dispatch({ type: dateRangeActionTypes.DATE_RANGE_FILTER_CHANGED, field: 'dateRange', dateField, value }),
        onToggle: () => toggle('dateRange')
    });
    return {
        filters,
        filtersList,
        setFilter,
        setSearch,
        resetFilters,
        onResetCache,
        sortBy: sort.sortBy,
        sortDirection: sort.sortDirection,
        setSortBy,
        hiddenColumns,
        setHiddenColumns
    };
}
